import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import mixpanel from '@/utils/mixpanel';
import { useUserStore } from '@/stores/user/userStore';

// SessionStorage keys
const RECORDING_ACTIVE_KEY = 'mixpanel_recording_active';
const ONBOARDING_COMPLETED_KEY = 'onboarding_completed';

// Define onboarding routes
const ONBOARDING_ROUTES = ['/create-account', '/set-password'];

// Routes that should stop recording (user left onboarding flow)
const EXIT_ROUTES = [
  '/log-in',
  '/sign-up',
  '/new-sign-up',
  '/forgot-password',
  '/reset-password',
];

// Helper: Check if path is an onboarding route
const isOnboardingRoute = (path) => {
  return ONBOARDING_ROUTES.some((route) => path.startsWith(route));
};

// Helper: Check if path is an authenticated route (not public/onboarding)
const isAuthenticatedRoute = (path) => {
  const publicRoutes = [
    ...ONBOARDING_ROUTES,
    ...EXIT_ROUTES,
    '/demo',
    '/weekly-webinar',
    '/ai-voice/demo',
    '/tiger-group/demo',
  ];
  return !publicRoutes.some((route) => path.startsWith(route));
};

/**
 * Component to manage Mixpanel session replay for onboarding flow
 *
 * Features:
 * - Starts recording on /create-account routes
 * - Stops when user completes onboarding (reaches /) or abandons flow
 * - Persists state across page refreshes using sessionStorage
 * - Prevents re-recording for already completed users
 * - Auto-stops after 30min inactivity (handled by Mixpanel)
 */
const MixpanelSessionReplayManager = () => {
  const location = useLocation();
  const isRecordingRef = useRef(false);
  const { auth, user } = useUserStore();

  // Initialize: Check sessionStorage for existing recording state
  useEffect(() => {
    const recordingActive = sessionStorage.getItem(RECORDING_ACTIVE_KEY) === 'true';
    if (recordingActive) {
      isRecordingRef.current = true;
    }
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    // Helper: Start recording
    const startRecording = () => {
      // Prevent starting if already recording
      if (isRecordingRef.current) {
        return;
      }

      // Check if user already completed onboarding (don't record again)
      const hasCompletedOnboarding = localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';
      if (hasCompletedOnboarding) {
        return;
      }

      // Check if user is already authenticated (logged in users don't need onboarding recording)
      if (auth?.access_token) {
        return;
      }

      try {
        mixpanel.start_session_recording();
        isRecordingRef.current = true;
        sessionStorage.setItem(RECORDING_ACTIVE_KEY, 'true');
      } catch (error) {
        console.error('Mixpanel: Failed to start session replay:', error);
      }
    };

    // Helper: Stop recording
    const stopRecording = (reason = 'completed') => {
      if (!isRecordingRef.current) {
        return;
      }

      try {
        // CRITICAL: Identify the user BEFORE stopping recording
        // This associates the session replay with the correct user
        if (reason === 'completed' && user && user.id) {
          mixpanel.identify(user.id);

          // Set user properties - matching App.jsx implementation
          mixpanel.people.set({
            '$email': user.email || '',
            '$name': user.full_name || user.name || '',
            'onboarding_completed': true,
          });

        }

        // Now stop the recording (it's associated with the correct user)
        mixpanel.stop_session_recording();
        isRecordingRef.current = false;
        sessionStorage.removeItem(RECORDING_ACTIVE_KEY);

        // Mark onboarding as completed if they reached dashboard
        if (reason === 'completed') {
          localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
        }
      } catch (error) {
        console.error('Mixpanel: Failed to stop session replay:', error);
      }
    };

    // Logic: Start recording when entering onboarding flow
    if (isOnboardingRoute(currentPath)) {
      startRecording();
    }

    // Logic: Stop recording if user completes onboarding (reaches dashboard)
    if (currentPath === '/' && isRecordingRef.current) {
      stopRecording('completed');
      return;
    }

    // Logic: Stop recording if user abandons onboarding (goes to login/signup)
    if (EXIT_ROUTES.includes(currentPath) && isRecordingRef.current) {
      stopRecording('abandoned - navigated to ' + currentPath);
      return;
    }

    // Logic: Stop recording if user navigates to authenticated routes (logged in)
    if (isAuthenticatedRoute(currentPath) && isRecordingRef.current) {
      stopRecording('user navigated to authenticated route');
      return;
    }
  }, [location.pathname, auth?.access_token, user]);

  return null;
};

export default MixpanelSessionReplayManager;
